import { useState, useRef, useEffect } from 'react';

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  new: { label: 'New', bg: 'bg-blue-100', text: 'text-blue-700' },
  contacted: { label: 'Contacted', bg: 'bg-yellow-100', text: 'text-yellow-700' },
  trial_booked: { label: 'Trial Booked', bg: 'bg-purple-100', text: 'text-purple-700' },
  trial_completed: { label: 'Trial Done', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  enrolled: { label: 'Enrolled', bg: 'bg-green-100', text: 'text-green-800' },
  lost: { label: 'Lost', bg: 'bg-gray-100', text: 'text-gray-600' },
};

const ALL_STATUSES = ['new', 'contacted', 'trial_booked', 'trial_completed', 'enrolled', 'lost'] as const;

export type LeadStatus = (typeof ALL_STATUSES)[number];

export function LeadStatusBadge({ status }: { status: string }) {
  const config = STATUS_CONFIG[status] ?? { label: status, bg: 'bg-gray-100', text: 'text-gray-600' };
  return (
    <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
}

export function LeadStatusSelect({
  status,
  onChange,
  disabled = false,
}: {
  status: string;
  onChange: (newStatus: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  const current = STATUS_CONFIG[status] ?? { label: status, bg: 'bg-gray-100', text: 'text-gray-600' };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setOpen(!open);
        }}
        disabled={disabled}
        className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${current.bg} ${current.text} hover:opacity-80 transition-opacity disabled:opacity-50`}
      >
        {current.label}
        <svg className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 w-40 py-1">
          {ALL_STATUSES.map((s) => {
            const cfg = STATUS_CONFIG[s]!;
            const isActive = s === status;
            return (
              <button
                key={s}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (s !== status) onChange(s);
                  setOpen(false);
                }}
                className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 transition-colors ${
                  isActive ? 'bg-gray-50 font-medium' : 'hover:bg-gray-50'
                }`}
              >
                <span className={`inline-block w-2 h-2 rounded-full ${cfg.bg.replace('100', '500')}`} />
                <span className={isActive ? cfg.text : 'text-gray-700'}>{cfg.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
